import React, { useEffect, useState } from 'react'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Avatar from '@mui/material/Avatar'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import NotificationsIcon from '@mui/icons-material/Notifications'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import PersonIcon from '@mui/icons-material/Person'
import LogoutIcon from '@mui/icons-material/Logout'
import { useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { addDetailsDriver } from '../../../store/slices/timesheet/timesheetPageDetailSlice'
import { useDispatch } from 'react-redux'
import { PublicClientApplication } from '@azure/msal-browser'
import { changePca } from '../../../store/slices/function/pca'
import { buildTenantConfigs, type Config } from '../../../app/configs'
import { ThemeProvider } from '@emotion/react'
import { defaultTheme } from '../../general/layout/ThemeProvider'
import pkj from '../../../../package.json'
import { RootState } from '../../../store/store'
import { useSelector } from 'react-redux'
import { useGetCompanyTenantsQuery } from '../../../store/api/tenants/tenantsApi'

// TODO: Add as needed
export const mapImageCompanyUrlTo = {
	'Eva Portimão': '/eva.png',
	Eva: '/eva.png',
	'Frota Azul Algarve': '/frotaazul.png',
	Maia: '/maia.png',
	Transol: '/transol.png',
	'Barraqueiro Transportes': 'BarraqueiroTransportes.png',
	Cityrama: '/cityrama.png',
}

export default function NavBar() {
	const [companyName, setCompanyName] = useState<string>('')
	const navigate = useNavigate()
	const { accounts, instance } = useMsal()
	const userName = accounts[0]?.name || 'Usuário'
	const userEmail = accounts[0]?.username || 'email@dominio.com'
	const imgRef = React.useRef<HTMLSpanElement>(null)
	const userRef = React.useRef<HTMLDivElement>(null)
	const [openCompanyMenu, setOpenCompanyMenu] = useState(false)
	const [openUserMenu, setOpenUserMenu] = useState(false)
	const [anchorElCompanyMenu, setAnchorElCompanyMenu] = useState<null | HTMLElement>(null)
	const [anchorElUserMenu, setAnchorElUserMenu] = useState<null | HTMLElement>(null)
	const dispatch = useDispatch()
	const appPca = useSelector((state: RootState) => state.pcaSlice)
	const [dynamicConfigs, setDynamicConfigs] = useState<Config>({})

	// Buscar tenants da API
	const { data: tenantsData } = useGetCompanyTenantsQuery()

	// Processar dados da API quando disponíveis
	useEffect(() => {
		if (tenantsData && tenantsData.length > 0) {
			const configs = buildTenantConfigs(tenantsData)
			setDynamicConfigs(configs)
		}
	}, [tenantsData])

	useEffect(() => {
		const storedCompany = localStorage.getItem('Company')

		if (!storedCompany) return

		const company = storedCompany as string

		if (instance.getConfiguration().auth.clientId === '') {
			if (dynamicConfigs[company]) {
				dispatch(
					changePca({
						pca: new PublicClientApplication(dynamicConfigs[company]),
					}),
				)
			} else if (appPca.pca) {
				// Usar o PCA do store se já estiver configurado
				dispatch(changePca({ pca: appPca.pca }))
			}
		}

		setCompanyName(company)
	}, [instance, dispatch, dynamicConfigs, appPca.pca])

	useEffect(() => {
		if (imgRef.current) {
			setAnchorElCompanyMenu(imgRef.current)
		}
		if (userRef.current) {
			setAnchorElUserMenu(userRef.current)
		}
	}, [imgRef, userRef])

	const handleUserClose = () => {
		setOpenUserMenu(false)
	}

	const handleCompanyClick = () => {
		setOpenCompanyMenu(true)
	}

	const handleUserClick = () => {
		setOpenUserMenu(true)
	}

	const handleCompanyClose = () => {
		setOpenCompanyMenu(false)
	}

	const handleLogout = async () => {
		setOpenUserMenu(false)
		try {
			await instance.logoutPopup()
		} catch (error) {
			console.error(error)
		}
		dispatch(
			addDetailsDriver({
				driverTableId: 0,
				driver: {
					driverName: '',
					timesheetDate: '',
					totalServices: 0,
					timeSheetId: null,
					status: '',
					comments: '',
				},
			}),
		)
		navigate('/', { replace: true })
	}

	const version: string = JSON.stringify(pkj.version).replace('"', '').replace('"', '')
	const companyLogoSrc = mapImageCompanyUrlTo[companyName as keyof typeof mapImageCompanyUrlTo]

	return (
		<ThemeProvider theme={defaultTheme}>
			<AppBar
				position='fixed'
				sx={{
					bgcolor: '#fff',
					boxShadow: 'none',
					height: '64px',
					zIndex: theme => theme.zIndex.drawer + 1,
				}}
			>
				<Toolbar
					sx={{
						minHeight: '64px',
						px: 2,
						display: 'flex',
						alignItems: 'center',
						gap: 2,
					}}
				>
					{/* Logo à esquerda */}
					<img
						src='/icon_logo.svg'
						alt='Logo'
						style={{ height: 40 }}
					/>
					<img src='/line-8.png' />
					{/* Nome e versão */}
					<Box sx={{ position: 'relative', marginBottom: 1 }}>
						<Typography
							variant='h6'
							sx={{
								color: 'var(--primary-dark)',
								fontFamily: 'var(--font-family)',
								fontWeight: 600,
								fontSize: '20px',
								lineHeight: 1.6,
							}}
						>
							Transporter
						</Typography>
						<Typography
							style={{
								fontFamily: 'var(--font-family)',
								fontWeight: 400,
								fontSize: '8px',
								lineHeight: 1.3,
								color: 'var(--primary)',
								position: 'absolute',
								bottom: '-8px',
								right: 0,
								margin: 0,
								padding: 0,
							}}
						>
							V {version}
						</Typography>
					</Box>
					<img src='/divider-7.png' />
					{/* Company Logo */}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							cursor: 'pointer',
						}}
						onClick={handleCompanyClick}
					>
						<Box
							component='span'
							sx={{ display: 'inline-block' }}
							ref={imgRef}
						>
							<img
								src={companyLogoSrc}
								alt={companyName}
								style={{ height: 32, marginRight: 10 }}
							/>
						</Box>
						{openCompanyMenu ? (
							<KeyboardArrowUpIcon sx={{ color: 'var(--primary)' }} />
						) : (
							<KeyboardArrowDownIcon sx={{ color: 'var(--primary)' }} />
						)}
					</div>
					<img src='/divider-7.png' />
					{/* Ícone engrenagem */}
					<IconButton>
						<SettingsOutlinedIcon sx={{ color: 'var(--primary)' }} />
					</IconButton>
					{/* Espaço para empurrar os ícones para a direita */}
					<Box sx={{ flexGrow: 1 }} />
					{/* Sino de notificação */}
					<IconButton>
						<NotificationsIcon sx={{ color: 'var(--primary)' }} />
					</IconButton>
					<img src='/divider-7.png' />
					{/* Avatar do usuário */}
					<Avatar
						alt={userName}
						src='/user_avatar.jpg' // TODO: substituir pela foto real do usuário qd disponível
						sx={{
							cursor: 'pointer',
							borderRadius: '100%',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							bgcolor: 'lightgrey 50% / cover no-repeat',
							width: 32,
							height: 32,
							fontSize: '20px',
							fontWeight: 400,
							lineHeight: 1,
						}}
						ref={userRef}
						onClick={handleUserClick}
					/>
				</Toolbar>
			</AppBar>
			<Menu
				id='company-menu'
				anchorEl={anchorElCompanyMenu}
				open={openCompanyMenu}
				onClose={handleCompanyClose}
				MenuListProps={{
					'aria-labelledby': 'basic-button',
				}}
				slotProps={{
					paper: {
						sx: {
							borderRadius: 3,
							p: 0,
							mt: 1,
							boxShadow:
								'0 3px 14px 2px rgba(9, 32, 86, 0.12), 0 8px 10px 1px rgba(9, 32, 86, 0.14), 0 5px 5px -3px rgba(9, 32, 86, 0.20)',
						},
					},
				}}
			>
				{/* Topo: Logo e nome da empresa */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						p: 2,
						borderBottom: '1px solid #f0f0f0',
					}}
				>
					<img
						src={companyLogoSrc}
						alt={companyName}
						style={{ height: 32, marginRight: 12 }}
					/>
					<Box>
						<Typography
							sx={{
								fontWeight: 500,
								fontSize: '13px',
								color: 'var(--primary-dark)',
							}}
						>
							{companyName}
						</Typography>
						<Typography
							sx={{
								fontWeight: 500,
								fontSize: '13px',
								color: 'var(--primary-dark)',
							}}
						>
							{companyName?.toLowerCase().includes('transol') && ' - Transportes e Turismo SA'}
							{companyName === 'FrotaAzul' && ' - Transportes e Turismo SA'}
							{companyName === 'Eva' && ' - Transportes e Turismo SA'}
						</Typography>
					</Box>
				</Box>
				{/* Título */}
				<Typography
					sx={{
						px: 2,
						pt: 2,
						pb: 1,
						fontSize: '11px',
						color: 'var(--primary-light)',
						fontWeight: 400,
					}}
				>
					Trocar de Empresa
				</Typography>
				{/* Lista de empresas */}
				<MenuItem
					onClick={handleCompanyClose}
					sx={{ py: 2, display: 'flex', alignItems: 'center' }}
				>
					<Avatar
						sx={{
							borderRadius: '100%',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							bgcolor: 'lightgrey 50% / cover no-repeat',
							width: 32,
							height: 32,
							mr: '12px',
							fontSize: '20px',
							fontWeight: 400,
							lineHeight: 1,
						}}
					>
						BA
					</Avatar>
					<Typography sx={{ fontWeight: 500, fontSize: '12px', color: 'var(--primary-dark)' }}>Empresa 1</Typography>
				</MenuItem>
				<MenuItem
					onClick={handleCompanyClose}
					sx={{ py: 2, display: 'flex', alignItems: 'center' }}
				>
					<Avatar
						sx={{
							borderRadius: '100%',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							bgcolor: 'lightgrey 50% / cover no-repeat',
							width: 32,
							height: 32,
							mr: '12px',
							fontSize: '20px',
							fontWeight: 400,
							lineHeight: 1,
						}}
					>
						MA
					</Avatar>
					<Typography sx={{ fontWeight: 500, fontSize: '12px', color: 'var(--primary-dark)' }}>Empresa 2</Typography>
				</MenuItem>
			</Menu>

			<Menu
				id='user-menu'
				anchorEl={anchorElUserMenu}
				open={openUserMenu}
				onClose={handleUserClose}
				MenuListProps={{
					'aria-labelledby': 'basic-button',
				}}
				sx={{
					boxShadow:
						'0 3px 14px 2px rgba(9, 32, 86, 0.12), 0 8px 10px 1px rgba(9, 32, 86, 0.14), 0 5px 5px -3px rgba(9, 32, 86, 0.20)',
				}}
				slotProps={{
					paper: {
						sx: { width: 260, borderRadius: 3, p: 0, mt: 1 },
					},
				}}
			>
				{/* Topo: Nome e e-mail do usuário */}
				<Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
					<Typography sx={{ fontWeight: 500, fontSize: '13px', color: 'var(--primary-dark)' }}>{userName}</Typography>
					<Typography sx={{ fontWeight: 400, fontSize: '12px', color: 'var(--primary-light)' }}>{userEmail}</Typography>
				</Box>
				<MenuItem
					onClick={() => navigate('/profile')}
					sx={{ py: 2, display: 'flex', alignItems: 'center' }}
				>
					<PersonIcon sx={{ color: 'var(--primary)', mr: '12px' }} />
					<Typography sx={{ fontWeight: 500, fontSize: '12px', color: 'var(--primary-dark)' }}>Perfil</Typography>
				</MenuItem>
				<MenuItem
					onClick={handleLogout}
					sx={{ py: 2, display: 'flex', alignItems: 'center' }}
				>
					<LogoutIcon sx={{ color: 'var(--primary)', mr: '12px' }} />
					<Typography sx={{ fontWeight: 500, fontSize: '12px', color: 'var(--primary-dark)' }}>Log out</Typography>
				</MenuItem>
			</Menu>
		</ThemeProvider>
	)
}
